import { useContext, useEffect, useRef, useState } from 'react';
import { SmartContext } from '../../library/Core/SmartContext';
import SchoolApiService, { PAGE_SIZE } from './SchoolApiService';
import moment from 'moment';

const MatchList = ({ data, slot }: any) => {
    const seriesData = data?.filter((item: any) => item.slot === slot);
    const [isExpanded, setIsExpanded] = useState(false);

    if (!seriesData || !seriesData.length) return null;

    let first, second, others;

    if (seriesData.length === 1) {
        [first] = seriesData;
        others = [];
    } else {
        [first, second, ...others] = seriesData;
    }

    return (
        <>
            <ul className="match-list">
                {first && (
                    <li>
                        {first.name} - {first.batch}
                    </li>
                )}
                {second && (
                    <li>
                        {second.name} - {second.batch}
                    </li>
                )}
            </ul>
            {isExpanded && (
                <ul className="match-list">
                    {others.map((item: any, index: number) => (
                        <li key={index}>
                            {item.name} - {item.batch}
                        </li>
                    ))}
                </ul>
            )}
            {others.length > 0 && (
                <button onClick={() => setIsExpanded(!isExpanded)}>{isExpanded ? '- Less' : `+ ${others.length} More`}</button>
            )}
        </>
    );
};

const Grid = () => {
    const { state, dispatch } = useContext(SmartContext);
    const studentList = state.data.studentList ?? [];
    const [alertVisible, setAlertVisible] = useState(false);

    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const isLoadingMoreRef = useRef(false);

    const handleClick = async (schoolId: any, groupId: any) => {
        try {
            const response: any = await SchoolApiService.apiKeyGenerate(schoolId, groupId ?? 0);
            const payload = response?.data ?? {};

            if (payload.success) {
                const newApiKey = payload.apiKey;
                const newCreated = payload.created ?? new Date();

                const updatedList = (state.data.studentList ?? []).map((row: any) =>
                    row.schoolId === schoolId
                        ? { ...row, apiKeys: newApiKey, created: newCreated }
                        : row
                );

                dispatch({
                    type: 'CONTROL_VALUE_CHANGE',
                    payload: { dataKey: 'studentList', value: updatedList, errorMessages: [] },
                });
                setAlertVisible(true);
            }
        } catch (err) {
            console.error('Failed to generate api key', err);
        }
    };

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (!entry.isIntersecting) return;

                const hasMore = state.internal?.hasMore;
                if (!hasMore || isLoadingMoreRef.current) return;

                const currentPage = state.internal?.page ?? 1;
                const nextPage = currentPage + 1;

                isLoadingMoreRef.current = true;
                dispatch({ type: 'SET_INTERNAL_STATE', payload: { key: 'isLoadingMore', value: true } });

                SchoolApiService.search(state.data.searchCriteria, state.routeInfo.pageName, nextPage, PAGE_SIZE)
                    .then((response: any) => {
                        const payload = response.data ?? {};
                        const rows = Array.isArray(payload) ? payload : (payload.data ?? []);
                        const total = Array.isArray(payload)
                            ? (state.internal?.total ?? rows.length)
                            : (payload.total ?? state.internal?.total ?? rows.length);

                        const merged = [...(state.data.studentList ?? []), ...rows];

                        dispatch({
                            type: 'CONTROL_VALUE_CHANGE',
                            payload: { dataKey: 'studentList', value: merged, errorMessages: [] },
                        });
                        dispatch({ type: 'SET_INTERNAL_STATE', payload: { key: 'page', value: nextPage } });
                        dispatch({ type: 'SET_INTERNAL_STATE', payload: { key: 'total', value: total } });
                        dispatch({ type: 'SET_INTERNAL_STATE', payload: { key: 'hasMore', value: merged.length < total } });
                    })
                    .catch((err) => {
                        console.error('Failed to load next page', err);
                    })
                    .finally(() => {
                        isLoadingMoreRef.current = false;
                        dispatch({ type: 'SET_INTERNAL_STATE', payload: { key: 'isLoadingMore', value: false } });
                    });
            },
            { root: null, rootMargin: '200px', threshold: 0 }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [
        state.internal?.hasMore,
        state.internal?.page,
        state.data.studentList,
        state.data.searchCriteria,
        state.routeInfo?.pageName,
    ]);

    const hasMore = state.internal?.hasMore;
    const isLoadingMore = state.internal?.isLoadingMore;
    const total = state.internal?.total ?? studentList.length;

    return (
        <div>
            {alertVisible && (
                <div
                    role="alert"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        padding: '12px 16px',
                        marginBottom: 12,
                        color: '#0f5132',
                        backgroundColor: '#d1e7dd',
                        border: '1px solid #badbcc',
                        borderRadius: 4,
                    }}>
                    <span style={{ color: '#0f5132', fontWeight: 500, fontSize: 14 }}>
                        Successfully Api Key Generated!
                    </span>
                    <button
                        type="button"
                        aria-label="Close"
                        onClick={() => setAlertVisible(false)}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            fontSize: 22,
                            lineHeight: 1,
                            color: '#0f5132',
                            cursor: 'pointer',
                            padding: '0 4px',
                        }}>
                        &times;
                    </button>
                </div>
            )}

            <div className="table-responsive attendee_dtl">
                <div className="d-flex justify-content-end align-items-center mb-2">
                    <h3 className="font-14 font-500 mb-0">
                        Showing {studentList.length} of {total} records
                    </h3>
                </div>

                <table className="table" style={{ minWidth: '1360px' }}>
                    <thead>
                        <tr>
                            <th>School Name</th>
                            <th>Group Name</th>
                            <th>Api Keys</th>
                            <th>Created Date</th>
                            <th>Api Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {studentList.map((student: any, idx: number) => (
                            <tr key={student.schoolId ?? idx}>
                                <td className="text-left">{student.schoolName}</td>
                                <td className="text-left">{student.schoolGroupName}</td>
                                <td className="text-left">{student.apiKeys}</td>
                                <td className="text-left">{student.created ? moment(student.created).format('DD-MMM-YYYY') : 'Null'}</td>
                                <td className="text-left">
                                    {!student.apiKeys ? (
                                        <button onClick={() => handleClick(student.schoolId, student.instituteGroupId)} className="btn-green">
                                            <span>Generate Api Key</span>
                                        </button>
                                    ) : (
                                        <button className="btn-green">
                                            <span>Done</span>
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div ref={sentinelRef} style={{ height: 1 }} />

                {isLoadingMore && (
                    <div className="text-center py-3 font-14 font-500">Loading more…</div>
                )}
                {!isLoadingMore && studentList.length === 0 && (
                    <div className="text-center py-3 font-14">No records found</div>
                )}
            </div>
        </div>
    );
};

export default Grid;
